import { c as _c } from "react/compiler-runtime";
import { DecoratorNode, $applyNodeReplacement } from 'lexical';
import type { Spread, LexicalNode, NodeKey, SerializedTextNode } from 'lexical';
import { TextMatchTransformer } from '@lexical/markdown';
import React, { ComponentProps, ReactNode } from 'react';
import Link from 'next/link';
import { match } from 'ts-pattern';
import { ChildrenProps } from 'components';
import { O } from 'ts-toolbelt';
import { SAvatar } from 'components/portal/SAvatar';
import { concat } from 'shared/collection';
import { uri } from 'shared/uri';
import { Mention, MentionProps } from 'shared/ui';
import { type ProfileUI } from 'components/portal/profileTypes';
import { useMantineColorScheme, useSafeMantineTheme } from '@mantine/core';
import { useRouter } from 'next/router';
export type SerializedMentionNode = Spread<{
  mention: MentionProps;
  type: 'mention';
}, SerializedTextNode>;
export class MentionNode extends DecoratorNode<ReactNode> {
  mention: MentionProps;
  static getType(): string {
    return 'mention';
  }
  constructor(mention: MentionProps, key?: NodeKey) {
    super(key);
    this.mention = mention;
  }
  static clone(node: MentionNode): MentionNode {
    return $createMentionNode(node.mention);
  }
  static importJSON(serializedNode: SerializedMentionNode): MentionNode {
    return $createMentionNode(serializedNode.mention);
  }
  exportJSON() {
    return {
      type: MentionNode.getType(),
      version: 1,
      mention: this.mention
    };
  }
  createDOM(): HTMLElement {
    return document.createElement('span');
  }
  updateDOM(): false {
    return false;
  }
  decorate(): ReactNode {
    return match(this.mention).with({
      type: 'question'
    }, q => <MentionQuestion {...q} />).with({
      type: 'profile'
    }, p => <MentionProfile {...p} />).exhaustive();
  }
}
export const MentionQuestion = t0 => {
  const $ = _c(17);
  const {
    path,
    title,
    children
  } = t0;
  const {
    colors
  } = useSafeMantineTheme();
  const {
    colorScheme
  } = useMantineColorScheme();
  const {
    query
  } = useRouter();
  let T0;
  let t1;
  let t2;
  let t3;
  if ($[0] !== colorScheme || $[1] !== colors.gray || $[2] !== path || $[3] !== query) {
    const [question_id, subquestion_id] = path.split("/");
    T0 = Link;
    t1 = "rounded p-1 text-xs";
    const t4 = colors.gray[colorScheme == "dark" ? 8 : 1];
    if ($[8] !== t4) {
      t2 = {
        backgroundColor: t4
      };
      $[8] = t4;
      $[9] = t2;
    } else {
      t2 = $[9];
    }
    t3 = uri(`/question/${question_id}`).mergeQuery({
      ...query,
      ...(subquestion_id ? {
        subquestion: subquestion_id
      } : {})
    });
    $[0] = colorScheme;
    $[1] = colors.gray;
    $[2] = path;
    $[3] = query;
    $[4] = T0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    T0 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
  }
  let t4;
  if ($[10] !== T0 || $[11] !== children || $[12] !== t1 || $[13] !== t2 || $[14] !== t3.url || $[15] !== title) {
    t4 = <T0 className={t1} style={t2} href={t3.url}>{title}{children}</T0>;
    $[10] = T0;
    $[11] = children;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3.url;
    $[15] = title;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
};
export const MentionQuestionBadge = t0 => {
  const $ = _c(15);
  let children;
  let className;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      className,
      style,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = style;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    style = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = concat("py-1 px-2 text-sm rounded-md", className).join(" ");
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = props.onClick ? "pointer" : undefined;
  let t3;
  if ($[7] !== style || $[8] !== t2) {
    t3 = {
      backgroundColor: "var(--mantine-color-gray-light)",
      cursor: t2,
      ...style
    };
    $[7] = style;
    $[8] = t2;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== children || $[11] !== props || $[12] !== t1 || $[13] !== t3) {
    t4 = <div className={t1} style={t3} {...props}>{children}</div>;
    $[10] = children;
    $[11] = props;
    $[12] = t1;
    $[13] = t3;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  return t4;
};
export const MentionContext = React.createContext<{
  profiles?: Record<string, ProfileUI>;
}>({});
export const MentionProfile = t0 => {
  const $ = _c(5);
  const {
    path: id,
    title
  } = t0;
  const {
    profiles
  } = React.useContext(MentionContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      root: {
        display: "inline-flex",
        top: "-0.1rem",
        verticalAlign: "middle"
      }
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = profiles?.[id];
  let t3;
  if ($[1] !== id || $[2] !== t2 || $[3] !== title) {
    t3 = <SAvatar size="sm" component="div" styles={t1} className="mx-[0.15em]" profile={{
      id,
      name: title,
      ...t2
    }} />;
    $[1] = id;
    $[2] = t2;
    $[3] = title;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
function $createMentionNode(props: MentionProps): MentionNode {
  return $applyNodeReplacement(new MentionNode(props));
}
function $isMentionNode(node: LexicalNode | null | undefined): node is MentionNode {
  return node instanceof MentionNode;
}
export const mentionTransformer = {
  type: 'text-match',
  dependencies: [MentionNode],
  export: node => $isMentionNode(node) ? Mention.markdown(node.mention) : null,
  importRegExp: Mention.markdownRegex,
  regExp: new RegExp(Mention.markdownPattern + '$'),
  replace: (textNode, match) => {
    const m = Mention.fromRegexMatch(match);
    if (!m) return;
    textNode.replace($createMentionNode(m)); // not sure this is right
  },
  trigger: ''
} as TextMatchTransformer;